import { Link } from 'gatsby'
import PropTypes from 'prop-types'
import React from 'react'
import VisibilitySensor from 'react-visibility-sensor'

import config from '../../../content/meta/config'
import avatar from '../../images/png/avatar.png'
import { FontLoadedContext, ScreenWidthContext } from '../../layouts'
import Menu from '../Menu'
import { UpdateButton } from '../UpdateButton/UpdateButton'

class Header extends React.Component {
  state = {
    fixed: false,
  }

  visibilitySensorChange = isVisible => {
    if (isVisible) {
      this.setState({ fixed: false })
    } else {
      this.setState({ fixed: true })
    }
  }

  getHeaderSize = () => {
    const fixed = this.state.fixed ? 'fixed' : ''
    const homepage = this.props.path === '/' ? 'homepage' : ''

    return `${fixed} ${homepage}`
  }

  render() {
    const { pages, path, theme } = this.props
    const { fixed } = this.state

    return (
      <>
        <header className={`header ${this.getHeaderSize()}`}>
          <Link to='/' className='logoType'>
            <div className='logo'>
              <img
                src={config.gravatarImgMd5 == '' ? avatar : config.gravatarImgMd5}
                alt={config.siteTitle}
              />
            </div>
            <div className='type'>
              <h1>{config.headerTitle}</h1>
              <h2>{config.headerSubTitle}</h2>
            </div>
          </Link>
          <UpdateButton />
          <FontLoadedContext.Consumer>
            {loaded => (
              <ScreenWidthContext.Consumer>
                {width => (
                  <Menu
                    path={path}
                    fixed={fixed}
                    screenWidth={width}
                    fontLoaded={loaded}
                    pages={pages}
                    theme={theme}
                  />
                )}
              </ScreenWidthContext.Consumer>
            )}
          </FontLoadedContext.Consumer>
        </header>
        <VisibilitySensor onChange={this.visibilitySensorChange}>
          <div className='sensor' />
        </VisibilitySensor>

        {/* --- STYLES --- */}
        <style jsx>{`
          .header {
            align-items: center;
            justify-content: center;
            background-color: var(--background-color-alt);
            display: flex;
            height: ${theme.header.height.default};
            position: relative;
            top: 0;
            width: 100%;
            z-index: 10;

            :global(a.logoType) {
              align-items: center;
              display: flex;
              flex-direction: column;
              color: var(--text-color-primary);

              .logo {
                flex-shrink: 0;
              }
            }

            &.homepage {
              position: absolute;
              background-color: transparent;
              height: ${theme.header.height.homepage};
              z-index: 10;
            }
          }

          h1 {
            font-size: ${theme.font.size.m};
            font-weight: ${theme.font.weight.standard};
            margin: ${theme.space.stack.xs};
          }

          h2 {
            font-weight: ${theme.font.weight.standard};
            font-size: ${theme.font.size.xxs};
            letter-spacing: 0;
            margin: 0;
          }

          .logo {
            border-radius: 65% 75%;
            border: 1px solid var(--line-color);
            display: inline-block;
            height: 44px;
            margin: ${theme.space.inline.default};
            overflow: hidden;
            width: 44px;
            transition: all 0.5s;

            .homepage & {
              height: 60px;
              width: 60px;
            }

            img {
              width: 100%;
            }
          }

          .sensor {
            display: block;
            position: absolute;
            bottom: 0;
            z-index: 1;
            left: 0;
            right: 0;
            height: 1px;
            top: ${path === '/' ? theme.header.height.homepage : theme.header.height.default};
          }

          @from-width tablet {
            .header {
              padding: ${theme.space.inset.l};

              &.homepage {
                height: ${theme.header.height.homepage};
              }
            }
          }

          @below desktop {
            .header.homepage {
              .logo {
                border: 2px solid rgba(255, 255, 255, 0.9);
                box-shadow: 0 2px 8px rgba(15, 23, 42, 0.25);
              }

              :global(a.logoType),
              h1 {
                color: var(--hero-header-text);
                font-weight: ${theme.font.weight.bold};
              }
              h2 {
                color: var(--hero-header-subtext);
              }
            }
          }

          @from-width desktop {
            .header {
              align-items: center;
              background-color: var(--background-color-primary);
              display: flex;
              position: absolute;
              top: 0;
              width: 100%;
              justify-content: space-between;
              transition: padding 0.5s;

              &.fixed {
                height: ${theme.header.height.fixed};
                background-color: var(--background-color-primary);
                left: 0;
                padding: 0 ${theme.space.m};
                position: fixed;
                top: 0;
                width: 100%;
                z-index: 10;

                h1 {
                  margin: ${theme.space.stack.xxs};
                }

                h2 {
                  display: none;
                }
              }

              &.homepage:not(.fixed) {
                background-color: transparent;

                :global(a.logoType),
                h1 {
                  color: var(--hero-header-text);
                  font-weight: ${theme.font.weight.bold};
                  transition: color ${theme.time.duration.default};
                }
                h2 {
                  color: var(--hero-header-subtext);
                }

                :global(a.logoType:hover h1) {
                  color: var(--color-brand-primary);
                }
              }
            }

            .header :global(a.logoType) {
              text-align: left;
              flex-direction: row;
              flex-shrink: 0;
              width: auto;
            }

            .logo {
              margin: ${theme.space.inline.default};

              .fixed & {
                height: 36px;
                width: 36px;
              }

              .header.homepage:not(.fixed) & {
                border: 2px solid rgba(255, 255, 255, 0.9);
                box-shadow: 0 2px 8px rgba(15, 23, 42, 0.25);
              }
            }
          }
        `}</style>
      </>
    )
  }
}

Header.propTypes = {
  pages: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
}

export default Header
